import { domainHash } from './primitives.js'

/**
 * Usernames.
 *
 * There is no registry and no authority, because there is no server. A
 * username is a *lookup key*, not an identity: `@alice` is simply the DHT
 * topic H("pine/username/v1", "alice"), and whoever is online announcing on
 * that topic answers.
 *
 * Two people can therefore claim the same name. Rather than pretend
 * otherwise, a search returns every signed claim it finds and the UI shows
 * them side by side with their account ids. In practice you get someone's
 * handle from them directly, so a collision is visible rather than a silent
 * hijack — and the account key stays the only thing that identifies a person.
 */

export const USERNAME_MIN = 3
export const USERNAME_MAX = 32

const VALID = /^[a-z0-9_]+$/

/** Strip the @, fold case, and reject anything that is not a-z, 0-9 or _. */
export function normalizeUsername (input) {
  return String(input || '')
    .trim()
    .replace(/^@+/, '')
    .toLowerCase()
}

export function usernameError (input) {
  const name = normalizeUsername(input)
  if (!name) return 'empty'
  if (name.length < USERNAME_MIN) return 'short'
  if (name.length > USERNAME_MAX) return 'long'
  if (!VALID.test(name)) return 'charset'
  if (/^_|_$/.test(name)) return 'underscore'
  return null
}

export function isValidUsername (input) {
  return usernameError(input) === null
}

/** The swarm topic a username resolves to. */
export function usernameTopic (input) {
  const name = normalizeUsername(input)
  if (!isValidUsername(name)) throw new Error('Invalid username: ' + input)
  return domainHash('pine/username/v1', name)
}

export function formatUsername (input) {
  const name = normalizeUsername(input)
  return name ? '@' + name : ''
}
